import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api from "@/lib/api";
import { ScanBarcode, X, Keyboard, ArrowUpFromLine } from "lucide-react";
import { toast } from "sonner";

export default function ScannerPage() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const dest = sp.get("dest") || "view"; // carico|scarico|view
  const [scanning, setScanning] = useState(true);
  const [manual, setManual] = useState("");
  const scannerRef = useRef(null);

  useEffect(() => {
    let html5 = null;
    let cancelled = false;
    if (scanning) {
      try {
        html5 = new Html5Qrcode("scanner-region");
        scannerRef.current = html5;
      } catch (err) {
        toast.error("Scanner non disponibile in questo browser");
        setScanning(false);
        return;
      }
      // Strategia camera per iOS/Android:
      // 1) Prova con facingMode "environment" (posteriore) — funziona su iPhone Safari senza dover leggere le label camera
      // 2) Fallback: enumera le camere e cerca "back/rear/environment" nella label
      // 3) Fallback finale: prima camera disponibile
      const cfg = { fps: 10, qrbox: 260, aspectRatio: 1 };
      const startWithFallback = async () => {
        // 1) facingMode environment
        try {
          await html5.start({ facingMode: { exact: "environment" } }, cfg, onScan, () => {});
          return;
        } catch (e1) { /* prova più permissivo */ }
        try {
          await html5.start({ facingMode: "environment" }, cfg, onScan, () => {});
          return;
        } catch (e2) { /* passa a enumerazione */ }
        // 2) enumera e cerca la posteriore
        try {
          const cams = await Html5Qrcode.getCameras();
          if (!cams || !cams.length) { toast.error("Nessuna fotocamera disponibile"); setScanning(false); return; }
          const back = cams.find(c => /back|rear|environment|posteriore|trasera/i.test(c.label || ""));
          const camId = back?.id || cams[cams.length - 1]?.id || cams[0].id; // ultima camera = spesso quella posteriore su iPhone
          await html5.start(camId, cfg, onScan, () => {});
        } catch (e3) {
          console.warn("[scanner] start failed:", e3?.message || e3);
          toast.error("Impossibile avviare la fotocamera posteriore");
          setScanning(false);
        }
      };
      if (!cancelled) startWithFallback();
    }
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        try {
          if (s.isScanning) s.stop().catch(() => {});
          if (typeof s.clear === "function") s.clear().catch?.(() => {});
        } catch (e) { /* ignore */ }
      }
    };
    // eslint-disable-next-line
  }, [scanning]);

  const handleCode = async (code) => {
    try {
      const { data } = await api.get("/products", { params: { q: code } });
      const found = data.find(p => p.codice_interno === code || p.codice_fornitore === code) || data[0];
      if (!found) { toast.error(`Codice non trovato: ${code}`); return; }
      if (dest === "carico") nav(`/carico?product=${found.id}`);
      else if (dest === "scarico") nav(`/scarico?product=${found.id}`);
      else nav(`/prodotti/${found.id}`);
    } catch (e) { toast.error("Errore lookup"); }
  };

  const onScan = async (decoded) => {
    if (scannerRef.current && scannerRef.current.isScanning) {
      await scannerRef.current.stop().catch(()=>{});
    }
    handleCode(decoded);
  };

  return (
    <div className="p-4 md:p-8 max-w-2xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Scanner</div>
          <h1 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight">
            {dest === "carico" ? "Scan per Carico" : dest === "scarico" ? "Scan per Scarico" : "Scan Codice"}
          </h1>
        </div>
        <button onClick={()=>nav(-1)} className="p-2 border border-zinc-700"><X size={20} /></button>
      </div>

      {scanning && (
        <div className="gm-card overflow-hidden mb-5">
          <div id="scanner-region" className="aspect-square bg-black" />
          <div className="p-3 text-xs uppercase tracking-widest text-zinc-400 text-center border-t border-zinc-800">
            Inquadra il barcode o QR code
          </div>
        </div>
      )}

      <div className="gm-card p-5">
        <div className="flex items-center gap-2 mb-3">
          <Keyboard className="text-zinc-500" size={18} />
          <span className="text-xs uppercase tracking-widest text-zinc-400">Inserimento manuale</span>
        </div>
        <form onSubmit={(e)=>{e.preventDefault(); handleCode(manual);}} className="flex gap-2">
          <input data-testid="scanner-manual-input" autoFocus value={manual} onChange={(e)=>setManual(e.target.value)} placeholder="Codice interno o fornitore" className="gm-input flex-1 font-mono-data" />
          <button type="submit" className="gm-btn-primary px-5 rounded-sm">OK</button>
        </form>
      </div>

      {/* Shortcut: scarico manuale senza scan (ricerca per descrizione) */}
      <div className="mt-4 border-t border-zinc-800 pt-4">
        <div className="text-xs uppercase tracking-widest text-zinc-400 mb-2">Non hai il barcode?</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <Link
            to="/scarico"
            className="gm-card p-4 border border-zinc-700 hover:border-[#4CAF50] hover:text-[#4CAF50] flex items-center gap-3"
            data-testid="scanner-manual-scarico"
          >
            <ArrowUpFromLine size={20} />
            <div>
              <div className="text-sm font-bold uppercase tracking-wide">Scarico manuale</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">Cerca il prodotto per nome e registra il consumo (es. grasso Repsol, olio, ricambi generici)</div>
            </div>
          </Link>
          <Link
            to="/scarico-mobile"
            className="gm-card p-4 border border-zinc-700 hover:border-[#4CAF50] hover:text-[#4CAF50] flex items-center gap-3"
            data-testid="scanner-mobile-scarico"
          >
            <ScanBarcode size={20} />
            <div>
              <div className="text-sm font-bold uppercase tracking-wide">Scarico Mobile guidato</div>
              <div className="text-[11px] text-zinc-500 mt-0.5">Flusso step-by-step con foto, cliente, cantiere, van</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
